<script setup>

// Imports
import { ref, computed, watch, shallowReactive, defineAsyncComponent } from 'vue'

// Props
const props = defineProps({
  pageBuilder: {
    type: Array,
    default: () => []
  }
})

const componentLoadersPreload = {
  // Hero Page
  heroAnimation: () => import('~/components/blocks/heroAnimation/index.vue'),
  heroSideImage: () => import('~/components/blocks/heroSideImage/index.vue'),
  heroSideImageV2: () => import('~/components/blocks/heroSideImageV2/index.vue'),
  heroSectionV1: () => import('~/components/blocks/heroSectionV1/index.vue'),
  heroSectionV2: () => import('~/components/blocks/heroSectionV2/index.vue'),
  heroSectionV3: () => import('~/components/blocks/heroSectionV3/index.vue'),
  heroSectionV4: () => import('~/components/blocks/heroSectionV4/index.vue'),
  heroCoverImage: () => import('~/components/blocks/heroCoverImage/index.vue'),
  heroWithText: () => import('~/components/blocks/heroWithText/index.vue'),
  heroCarousel: () => import('~/components/blocks/heroCarousel/index.vue'),
  heroWithForm: () => import('~/components/blocks/heroWithForm/index.vue'),
  heroWithFormDelight: () => import('~/components/blocks/heroWithFormDelight/index.vue'),
  heroWithFormV2: () => import('~/components/blocks/heroWithFormV2/index.vue'),
  heroAssessment: () => import('~/components/blocks/heroAssessment/index.vue'),
  demoCardCarousel: () => import('~/components/blocks/demoCardCarousel/index.vue'),
  heroVideoTimestamps: () => import('~/components/blocks/heroVideoTimestamps/index.vue'),
  heroSimple: () => import('~/components/blocks/heroSimple/index.vue'),
  heroWithImageAndTextCarousel: () => import('~/components/blocks/heroWithImageAndTextCarousel/index.vue'),

  // Hero Seasons & Events
  heroSeasonsEvents: () => import('~/components/blocks/heroSeasonsEvents/index.vue'),
  heroFeatureColumns: () => import('~/components/blocks/heroFeatureColumns/index.vue'),
  agendaSchedule: () => import('~/components/blocks/agendaSchedule/index.vue'),


  // Page Content
  logoGarden: () => import('~/components/blocks/logoGarden/index.vue'),
}

const componentLoadersLazy = {
  // Page Content
  cardGrid: () => import('~/components/blocks/cardGrid/index.vue'),
  cta: () => import('~/components/blocks/cta/index.vue'),
  ctaContained: () => import('~/components/blocks/cta/ctaContained.vue'),
  ctaBackgroundImage: () => import('~/components/blocks/cta/ctaBackgroundImage.vue'),
  faq: () => import('~/components/blocks/faq/index.vue'),
  faqCentered: () => import('~/components/blocks/faq/faqCentered.vue'),
  informationAccordion: () => import('~/components/blocks/accordion/informationAccordion.vue'),
  accordionMedia: () => import('~/components/blocks/accordion/accordionMedia.vue'),
  stats: () => import('~/components/blocks/stats/index.vue'),
  statsDelight: () => import('~/components/blocks/statsDelight/index.vue'),
  lottie: () => import('~/components/blocks/lottie/index.vue'),
  featureTabs: () => import('~/components/blocks/featureTabs/index.vue'),
  quote: () => import('~/components/blocks/quote/index.vue'),
  textAndMedia: () => import('~/components/blocks/textAndMedia/index.vue'),
  textAndMediaDelight: () => import('~/components/blocks/textAndMediaDelight/index.vue'),
  oneboardGuide: () => import('~/components/blocks/oneboardGuide/index.vue'),
  resources: () => import('~/components/blocks/resources/index.vue'),
  autoplayProductGuide: () => import('~/components/blocks/autoplayProductGuide/index.vue'),
  groupedTextMediaSection: () => import('~/components/blocks/groupedTextMediaSection/index.vue'),
  groupedFeatures: () => import('~/components/blocks/groupedFeatures/index.vue'),
  textBanner: () => import('~/components/blocks/textBanner/index.vue'),
  ctaAdvanced: () => import('~/components/blocks/ctaAdvanced/index.vue'),
  ctaCompact: () => import('~/components/blocks/ctaCompact/index.vue'),
  releaseTimeline: () => import('~/components/blocks/releaseTimeline/index.vue'),
  integrations: () => import('~/components/blocks/integrations/index.vue'),
  compareTables: () => import('~/components/blocks/compareTables/index.vue'),
  questions: () => import('~/components/blocks/questions/index.vue'),
  imageTextStack: () => import('~/components/blocks/imageTextStack/index.vue'),
  quotesCarousel: () => import('~/components/blocks/quotesCarousel/index.vue'),
  roiCalculator: () => import('~/components/blocks/roiCalculator/index.vue'),
  textMediaMobileSlider: () => import('~/components/blocks/textMediaMobileSlider/index.vue'),
  textAndMediaExtended: () => import('~/components/blocks/textAndMediaExtended/index.vue'),
  customHtml: () => import('~/components/blocks/customHtml/index.vue'),
  textAndImage: () => import('~/components/blocks/textAndImage/index.vue'),
  metricsBar: () => import('~/components/blocks/metricsBar'),
  comparisonTable: () => import('~/components/blocks/comparisonTable'),
  featurePages: () => import('~/components/blocks/featurePages/index.vue'),
  multiStepForm: () => import('~/components/blocks/multiStepForm/index.vue'),
  multiStepFormResult: () => import('~/components/blocks/multiStepFormResult/index.vue'),
  quoteHighlight: () => import('~/components/blocks/quoteHighlight/index.vue'),
  scoreBreakdown: () => import('~/components/blocks/scoreBreakdown/index.vue'),
  pillars: () => import('~/components/blocks/pillars/index.vue'),
  benefits: () => import('~/components/blocks/benefits/index.vue'),
  featureList: () => import('~/components/blocks/featureList/lazy.vue'),
  faqDelight: () => import('~/components/blocks/faq/faqDelight.vue'),
  informationAccordionDelight: () => import('~/components/blocks/accordion/informationAccordionDelight.vue'),
  industryShowcase: () => import('~/components/blocks/industryShowcase/index.vue'),
  featureGrid: () => import('~/components/blocks/featureGrid/index.vue'),
  contextSources: () => import('~/components/blocks/contextSources/index.vue'),
  featureTiles: () => import('~/components/blocks/featureTiles/index.vue'),
  mediaHighlight: () => import('~/components/blocks/mediaHighlight/index.vue'),
  headingText: () => import('~/components/blocks/headingText/index.vue'),
  scrollShowcase: () => import('~/components/blocks/scrollShowcase/index.vue'),
  animatedText: () => import('~/components/blocks/animatedText/index.vue'),
  animatedHighlightText: () => import('~/components/blocks/animatedHighlightText/index.vue'),
  benefitsSection: () => import('~/components/blocks/benefitsSection/index.vue'),
  contentBlock: () => import('~/components/blocks/contentBlock/index.vue'),
  openPositions: () => import('~/components/blocks/openPositions/index.vue'),
  oversightShowcase: () => import('~/components/blocks/oversightShowcase/index.vue'),
  lottieAnimation: () => import('~/components/blocks/lottieAnimation/index.vue'),
  chapterList: () => import('~/components/blocks/chapterList/index.vue'),
  metricsAndTips: () => import('~/components/blocks/metricsAndTips/index.vue'),
  featureBlockWithVisual: () => import('~/components/blocks/featureBlockWithVisual/index.vue'),
  caseStudiesCarousel: () => import('~/components/blocks/caseStudiesCarousel/index.vue'),
  stageList: () => import('~/components/blocks/stageList/index.vue'),
  permissionAudit: () => import('~/components/blocks/permissionAudit/index.vue'),
  quotesCarouselV2: () => import('~/components/blocks/quotesCarouselV2/index.vue'),
  metricsBarV2: () => import('~/components/blocks/metricsBarV2/index.vue'),
  exploreContent: () => import('~/components/blocks/exploreContent/index.vue'),
  industryBlueprint: () => import('~/components/blocks/industryBlueprint/index.vue'),
  voiceDemo: () => import('~/components/blocks/voiceDemo/index.vue'),
  cardWithVideo: () => import('~/components/blocks/cardWithVideo/index.vue'),
  historyAndUpcoming: () => import('~/components/blocks/historyAndUpcoming/index.vue'),
  resourceHub: () => import('~/components/blocks/resourceHub/index.vue'),
  team: () => import('~/components/blocks/team/index.vue'),
  zendeskPricingCalculator: () => import('~/components/blocks/zendeskPricingCalculator/index.vue'),
  roiCalculatorV2: () => import('~/components/blocks/roiCalculatorV2/index.vue'),
  customerStories: () => import('~/components/blocks/customerStories/index.vue'),
  useCasesSlider: () => import('~/components/blocks/useCasesSlider/index.vue'),
  statsMarquee: () => import('~/components/blocks/statsMarquee/index.vue'),

  // Content Seasons & Events
  horizontalScrollCards: () => import('~/components/blocks/horizontalScrollCards/index.vue'),
  mediaSpotlight: () => import('~/components/blocks/mediaSpotlight/index.vue'),
  textForm: () => import('~/components/blocks/textForm/index.vue'),
  eventsGrid: () => import('~/components/blocks/eventsGrid/index.vue'),
  statementBanner: () => import('~/components/blocks/statementBanner/index.vue'),
  shareSection: () => import('~/components/blocks/shareSection/index.vue'),
  agendaGrid: () => import('~/components/blocks/agendaGrid/index.vue'),
  location: () => import('~/components/blocks/location/index.vue'),
  contentIntro: () => import('~/components/blocks/contentIntro/index.vue'),
  contentTwoColumnGrid: () => import('~/components/blocks/contentTwoColumnGrid/index.vue'),
  agendaGrouped: () => import('~/components/blocks/agendaGrouped/index.vue'),
  agendaGroupedV2: () => import('~/components/blocks/agendaGroupedV2/index.vue'),
  reasons: () => import('~/components/blocks/reasons/index.vue'),
  hostProfile: () => import('~/components/blocks/hostProfile/index.vue'),
  metricCards: () => import('~/components/blocks/metricCards/index.vue'),
  industryShowcaseCarousel: () => import('~/components/blocks/industryShowcaseCarousel/index.vue'),
}

const componentLoaders = {
  ...componentLoadersPreload,
  ...componentLoadersLazy,
  missingComponent: () => import('~/components/globals/pageBuilder/missingComponent.vue'),
}

const componentCache = shallowReactive({})

const lowerCamelizeTypename = (value) => {
  const str = String(value ?? '')
  if (!str) return str

  // Handle cases like:
  // - FaqDelight -> faqDelight
  // - FAQDelight -> faqDelight
  // - ROIcalculator -> roiCalculator
  const match = str.match(/^[A-Z]+(?=[A-Z][a-z]|[a-z]|$)/)
  if (match) {
    const lead = match[0]
    const rest = str.slice(lead.length)
    return lead.toLowerCase() + rest
  }

  return str.charAt(0).toLowerCase() + str.slice(1)
}

const resolveLoaderKeyDetailed = (typename) => {
  const base = String(typename ?? '').replace('_Entry', '')
  if (!base) {
    return {
      base: null,
      candidates: [],
      key: null,
      hasLazy: false,
      hasPreload: false,
    }
  }

  const candidates = [
    base,
    base.charAt(0).toLowerCase() + base.slice(1),
    lowerCamelizeTypename(base),
  ].filter(Boolean)

  for (const key of candidates) {
    const hasLazy = Boolean(componentLoadersLazy[key])
    const hasPreload = Boolean(componentLoadersPreload[key])
    if (hasLazy || hasPreload) {
      return { base, candidates, key, hasLazy, hasPreload }
    }
  }

  return {
    base,
    candidates,
    key: null,
    hasLazy: false,
    hasPreload: false,
  }
}

const missingComponentModule = await componentLoaders.missingComponent()
const missingComponent = missingComponentModule.default || missingComponentModule

const normalizedTypeFromBlock = (block) => {
  const typename = block?.__typename
  if (!typename) return null
  return resolveLoaderKeyDetailed(typename).key
}

const getComponent = (block) => {
  const typename = block?.__typename
  const { base, candidates, key, hasLazy, hasPreload } =
    resolveLoaderKeyDetailed(typename)

  if (!key) return null

  return componentCache[key] || null
}

const getLoadingModeForBlock = (block) => {
  const key = normalizedTypeFromBlock(block)
  if (!key) return null
  if (componentLoadersLazy[key]) return 'no-preload'
  if (componentLoadersPreload[key]) return 'preload'
  return null
}

const typenames = computed(() =>
  props.pageBuilder.map((block) => block?.__typename).filter(Boolean)
)
const preloadComponents = async (types) => {
  const uniqueTypes = [
    ...new Set(
      types
        .map((type) => resolveLoaderKeyDetailed(type).key)
        .filter(Boolean)
    ),
  ]
  await Promise.all(
    uniqueTypes.map(async (type) => {
      if (componentCache[type]) return

      const lazyLoader = componentLoadersLazy[type]
      if (lazyLoader) {
        // SSR: load module so content is rendered server-side.
        // Client: don't preload, use async component (code loads when needed).
        if (import.meta.server) {
          const module = await lazyLoader()
          componentCache[type] = module.default || module
        } else {
          componentCache[type] = defineAsyncComponent(async () => {
            const module = await lazyLoader()
            return module.default || module
          })
        }
        return
      }

      const preloadLoader = componentLoadersPreload[type]
      if (!preloadLoader) return

      const module = await preloadLoader()
      componentCache[type] = module.default || module
    })
  )
}

const componentsLoaded = ref(false)
let loadId = 0

// Page Builder canvas keying (composables/pageBuilderCanvas.ts): plain `block.id` outside
// the builder; revisioned inside it so an edited block can remount in place.
const blockKey = usePbBlockKey()

const runPreload = async () => {
  const currentLoad = ++loadId
  const types = typenames.value
  if (types.length === 0) {
    componentsLoaded.value = true
    return
  }

  // In-place data refreshes (Page Builder canvas) re-trigger this with already-loaded types.
  // Don't drop componentsLoaded in that case — the v-if would unmount EVERY block for a tick
  // (scroll jump, all widgets re-init) just to remount the same tree.
  const missing = types.filter((type) => {
    const key = resolveLoaderKeyDetailed(type).key
    return key && !componentCache[key]
  })
  if (missing.length === 0) {
    componentsLoaded.value = true
    return
  }

  componentsLoaded.value = false
  await preloadComponents(types)
  if (currentLoad === loadId) {
    componentsLoaded.value = true
  }
}

await runPreload()

watch(typenames, () => {
  runPreload()
})

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
<div class="page-builder">
  <template v-if="componentsLoaded">
    <template v-for="(block, index) in pageBuilder"
      :key="blockKey(block)"
    >
      
      <component
        v-if="getComponent(block)"
        :is="getComponent(block)"
        :block="block"
        :data-pb-id="block.id"
        :data-pb-type="block.__typename"
        :data-pb-index="index"
        :pb-index="index"
        v-bind="getLoadingModeForBlock(block)
          ? { 'data-loading': getLoadingModeForBlock(block) }
          : undefined"
      />

      <component v-else
        :is="missingComponent"
        :typename="block.__typename"
      />

    </template>
  </template>
</div>
</template>
